import React, { useEffect, useState } from "react";
import { Download, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  getDeferredInstallPrompt,
  clearDeferredInstallPrompt,
  isAppInstalled,
} from "@/lib/pwa";

const DISMISS_KEY = "efcm_pwa_install_dismissed_v1";
const DISMISS_TTL_DAYS = 90;

function isDismissed() {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const at = new Date(raw);
    const diff = (Date.now() - at.getTime()) / (1000 * 60 * 60 * 24);
    return diff < DISMISS_TTL_DAYS;
  } catch {
    return false;
  }
}

function markDismissed() {
  try {
    localStorage.setItem(DISMISS_KEY, new Date().toISOString());
  } catch {
    /* ignore */
  }
}

function detectIOS() {
  const ua = navigator.userAgent || "";
  return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
}

/**
 * Shows a bottom banner inviting the user to install the app.
 *
 *  - On Chrome/Edge/Android: uses the standard `beforeinstallprompt` event.
 *  - On iOS Safari: shows a manual instruction (there is no programmatic
 *    prompt on iOS as of 2026).
 */
export default function PWAInstallBanner() {
  const [available, setAvailable] = useState(false);
  const [ios, setIos] = useState(false);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    if (isAppInstalled()) {
      setVisible(false);
      return;
    }
    if (isDismissed()) {
      setVisible(false);
      return;
    }
    if (detectIOS()) {
      setIos(true);
      setAvailable(true);
      return;
    }
    // Native install prompt already captured before React mounted?
    if (getDeferredInstallPrompt()) setAvailable(true);

    const onAvail = () => setAvailable(true);
    const onInstalled = () => setVisible(false);
    window.addEventListener("pwa-install-available", onAvail);
    window.addEventListener("pwa-installed", onInstalled);
    return () => {
      window.removeEventListener("pwa-install-available", onAvail);
      window.removeEventListener("pwa-installed", onInstalled);
    };
  }, []);

  const install = async () => {
    const deferred = getDeferredInstallPrompt();
    if (!deferred) return;
    deferred.prompt();
    await deferred.userChoice.catch(() => null);
    clearDeferredInstallPrompt();
    setVisible(false);
  };

  const dismiss = () => {
    markDismissed();
    setVisible(false);
  };

  if (!visible || !available) return null;

  return (
    <div
      data-testid="pwa-install-banner"
      className="fixed bottom-3 left-3 right-3 md:left-auto md:right-6 md:bottom-6 md:max-w-xs z-50 rounded-xl border border-purple-200 bg-white shadow-xl p-3 pr-2 flex items-start gap-2.5"
    >
      <img
        src="/icon-192.png"
        alt=""
        className="w-9 h-9 rounded-lg shadow-sm shrink-0"
      />
      <div className="flex-1 min-w-0">
        <div className="font-bold text-xs text-slate-900 leading-tight">
          Instala la app en tu móvil
        </div>
        <div className="text-[11px] text-slate-600 mt-0.5 leading-snug">
          {ios ? (
            <>
              Pulsa <span className="font-semibold">Compartir</span> ⬆ y luego{" "}
              <span className="font-semibold">"Añadir a pantalla de inicio"</span>.
            </>
          ) : (
            <>Accede más rápido desde tu pantalla de inicio.</>
          )}
        </div>
        {!ios && (
          <div className="mt-2">
            <Button
              size="sm"
              className="bg-purple-700 hover:bg-purple-800 text-white h-7 px-3 text-xs"
              onClick={install}
              data-testid="pwa-install-button"
            >
              <Download className="w-3.5 h-3.5 mr-1.5" />
              Instalar
            </Button>
          </div>
        )}
      </div>
      <button
        aria-label="Cerrar"
        onClick={dismiss}
        data-testid="pwa-install-dismiss"
        className="p-1 -mr-1 text-slate-400 hover:text-slate-600 shrink-0"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
